import { CornerUpRight, Minus, MoveUpRight, Slash } from "lucide-react";
import {
  useCanvasShapeAndEdgeStyle,
  type ShapeStrokeStyle,
} from "../../../../hooks/useCanvasShapeAndEdgeStyle";
import type { ConnectorPreset } from "../../../../hooks/useConnectorPlacement";
import {
  ALL_SHAPE_TYPES,
  SHAPE_LABELS,
  type ShapeType,
} from "../../../../nodes/shapes/index";
import {
  CANVAS_NO_FILL_VALUE,
  SHAPE_COLORS,
} from "../../../../nodes/shared/CanvasColorPalettes";
import {
  CanvasStrokeTypePreview,
  CanvasStrokeWidthPreview,
} from "../../../../nodes/shared/CanvasToolbarGraphics";
import { CANVAS_STROKE_WIDTHS } from "../../../../types/canvasNodeData";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_TILE_GLYPH,
} from "../../canvasToolbarComposition";
import { SHAPE_LUCIDE_ICON } from "../../canvasToolbarIcon";
import {
  SwatchGrid,
  TileGrid,
  Tray,
  TrayDeck,
  TrayPair,
  TraySection,
} from "../CanvasTrayContent";

interface ConnectorPresetDef extends ConnectorPreset {
  key: string;
  name: string;
  node: React.ReactNode;
}

const CurvedArrow = () => (
  <svg
    width={CANVAS_TILE_GLYPH}
    height={CANVAS_TILE_GLYPH}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M 2 13 Q 3 4 12 4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      fill="none"
    />
    <polyline
      points="9,1 12,4 9,7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const glyph = (Icon: typeof Minus) => (
  <Icon
    size={CANVAS_TILE_GLYPH}
    strokeWidth={CANVAS_GLYPH_STROKE}
    aria-hidden="true"
  />
);

/** The product's connector lines: three arrows and a plain line. */
export const CONNECTOR_PRESETS: ConnectorPresetDef[] = [
  {
    key: "straight-arrow",
    name: "Straight arrow",
    arrowMode: "end",
    curve_type: "straight",
    node: glyph(MoveUpRight),
  },
  {
    key: "curved-arrow",
    name: "Curved arrow",
    arrowMode: "end",
    curve_type: "smooth",
    node: <CurvedArrow />,
  },
  {
    key: "step-arrow",
    name: "Step arrow",
    arrowMode: "end",
    curve_type: "step",
    node: glyph(CornerUpRight),
  },
  {
    key: "line",
    name: "Line",
    arrowMode: "none",
    curve_type: "straight",
    node: glyph(Minus),
  },
];

const STROKE_TYPES: { key: ShapeStrokeStyle; name: string }[] = [
  { key: "solid", name: "Solid" },
  { key: "dashed", name: "Dashed" },
  { key: "dotted", name: "Dotted" },
  { key: "none", name: "None" },
];

const WIDTH_NAMES: Record<number, string> = {
  1: "Thin",
  2: "Medium",
  4: "Heavy",
};

interface ShapesTrayProps {
  shape: ShapeType | null;
  onShape: (shape: ShapeType) => void;
  connectorPreset: ConnectorPreset | null;
  onConnector: (preset: ConnectorPreset) => void;
}

/**
 * One palette, then how it is painted. Bottom-up: the shape row nearest the
 * bar, the lines directly above it (the same pick, closed versus open),
 * then fill, then stroke. Every row spans the band.
 */
export function ShapesTray({
  shape,
  onShape,
  connectorPreset,
  onConnector,
}: ShapesTrayProps) {
  const {
    activeShapeColor,
    activeShapeNoFill,
    activeShapeStrokeStyle,
    activeShapeStrokeWidth,
    setShapeStyle,
  } = useCanvasShapeAndEdgeStyle();

  const widths = CANVAS_STROKE_WIDTHS.map((width) => ({
    key: String(width),
    name: WIDTH_NAMES[width],
    node: (
      <CanvasStrokeWidthPreview
        width={width}
        active={activeShapeStrokeWidth === width}
      />
    ),
  }));
  const types = STROKE_TYPES.map((t) => ({
    key: t.key,
    name: t.name,
    node:
      t.key === "none" ? (
        glyph(Slash)
      ) : (
        <CanvasStrokeTypePreview
          strokeStyle={t.key}
          active={activeShapeStrokeStyle === t.key}
        />
      ),
  }));
  const fills = SHAPE_COLORS.map((c) => ({
    key: c.value,
    name: c.name,
    tint: c.value === CANVAS_NO_FILL_VALUE ? undefined : c.swatchFill,
  }));
  const fill = activeShapeNoFill ? CANVAS_NO_FILL_VALUE : activeShapeColor;
  const lines = CONNECTOR_PRESETS.map(({ key, name, node }) => ({
    key,
    name,
    node,
  }));
  const line =
    CONNECTOR_PRESETS.find(
      (p) =>
        connectorPreset &&
        p.arrowMode === connectorPreset.arrowMode &&
        p.curve_type === connectorPreset.curve_type,
    )?.key ?? null;
  const shapes = ALL_SHAPE_TYPES.map((s) => ({
    key: s,
    name: SHAPE_LABELS[s],
    node: glyph(SHAPE_LUCIDE_ICON[s]),
  }));

  return (
    <Tray>
      <TrayDeck>
        <TrayPair>
          <TraySection ariaLabel="Stroke width">
            <TileGrid
              items={widths}
              value={String(activeShapeStrokeWidth)}
              onChange={(k) => setShapeStyle({ strokeWidth: Number(k) })}
              columns={widths.length}
            />
          </TraySection>
          <TraySection ariaLabel="Stroke type">
            <TileGrid
              items={types}
              value={activeShapeStrokeStyle}
              onChange={(k) =>
                setShapeStyle({ strokeStyle: k as ShapeStrokeStyle })
              }
              columns={types.length}
            />
          </TraySection>
        </TrayPair>
        <TraySection ariaLabel="Fill">
          <SwatchGrid
            items={fills}
            value={fill}
            onChange={(k) =>
              k === CANVAS_NO_FILL_VALUE
                ? setShapeStyle({ noFill: true })
                : setShapeStyle({ color: k, noFill: false })
            }
            columns={fills.length}
          />
        </TraySection>
        <TraySection ariaLabel="Line">
          <TileGrid
            items={lines}
            value={line}
            onChange={(k) => {
              const preset = CONNECTOR_PRESETS.find((p) => p.key === k);
              if (preset)
                onConnector({
                  arrowMode: preset.arrowMode,
                  curve_type: preset.curve_type,
                });
            }}
            columns={lines.length}
          />
        </TraySection>
        <TraySection ariaLabel="Shape">
          <TileGrid
            items={shapes}
            value={line ? null : shape}
            onChange={(k) => onShape(k as ShapeType)}
            columns={shapes.length}
          />
        </TraySection>
      </TrayDeck>
    </Tray>
  );
}
